import {
	ChangeEvent,
	Fragment,
	useEffect,
	useRef,
	RefObject,
	useContext
} from 'react';
import {
	H3,
	H4,
	Spacer,
	Text,
	Tile,
	InfoChip,
	Caption,
	TextArea,
	TextInput,
	UploadButton,
	Checkbox,
	Grid,
	FileUploadChip,
	toast
} from '@components';
import { EditShipment } from '@state';
import { useRecoilState } from 'recoil';
import { formatDate, groupCargosByStop } from '@utils';
import styles from '../EditShipment.module.scss';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import {
	CHIP_SIZE,
	TShipmentOrder,
	TShipmentAddressDoc,
	TUploadFileRes,
	TPagedDialogContext,
	BUTTON_TYPES
} from '@types';
import { v4 as uuid } from 'uuid';
import { useDeleteShipmentDoc, useRemoveShipmentEosDoc } from '@api';
import { EditShipmentContext } from '../EditShipment.Context';

type TProps = {
	pagesRef?: RefObject<HTMLElement>;
	context?: TPagedDialogContext;
}

export const DriverInfo = (props: TProps) => {
	const { context, pagesRef } = props;
	const ctx = useContext(EditShipmentContext);
	const inputsDisabled = ctx?.access.pages.driverInfo.fields.inputs.disabled ?? false;
	const documentsDisabled = ctx?.access.pages.driverInfo.fields.documents.disabled ?? false;
	const isScrollRef = useRef(-1);
	const subpages = useRef<(HTMLDivElement | null)[]>([]);
	const [shipment, setShipment] = useRecoilState(EditShipment);
	const { callDeleteDoc } = useDeleteShipmentDoc();
	const { callRemoveEosDoc } = useRemoveShipmentEosDoc();
	// Cargos collected from the same place are one stop, so an edit applies to every cargo on it.
	const { pickups: pickupGroups, dropoffs: dropoffGroups } = groupCargosByStop(shipment?.cargos ?? []);
	const addFilesForUpload = (files: FileList | null, dest: 'pickup' | 'dropoff', index: number) => {
		if (files) {
			const newDocs: Partial<TShipmentAddressDoc>[] = [];
			Array.from(files).forEach((file) => {
				const fileSize = +(file.size / (1024*1024)).toFixed(0);
				if (fileSize <= 80) {
					const doc = {
						file,
						document: { name: file.name },
						tempID: uuid()
					}
					newDocs.push(doc);
				} else {
					toast.error('A file you are trying to upload is too big', { id: 'FILE_TO_LARGE' });
				}
			});
			shipment && setShipment({
				...shipment,
				cargos: shipment.cargos.map((cargo, i) => {
					if (i !== index) return cargo;
					return {
						...cargo,
						[dest]: {
							...cargo[dest],
							shipment_address_documents: [
								...cargo[dest].shipment_address_documents ?? [],
								...newDocs
							]
						}
					}
				})
			});
			if (ctx?.setTouched) ctx.setTouched(true);
		}
	}
	const removeFile = (id: string | number, dest: 'pickup' | 'dropoff', index: number) => {
		const doc = shipment?.cargos[index][dest].shipment_address_documents?.find((d) =>
			(d.id ? `${d.id}` : d.tempID ?? d.shipmentAPIid ?? '') === `${id}`
		);

		const deleteCall = doc?.shipment_address_id ? callDeleteDoc : callRemoveEosDoc;
		deleteCall({ routes: [`${id}`] }).then((res) => {
			if (res) {
				shipment && setShipment({
					...shipment,
					cargos: shipment.cargos.map((cargo, i) => {
						if (i !== index) return cargo;
						return {
							...cargo,
							[dest]: {
								...cargo[dest],
								shipment_address_documents: cargo[dest].shipment_address_documents?.filter((doc) => `${doc.id}` !== `${id}`)
							}
						}
					})
				});
				if (ctx?.setTouched) ctx.setTouched(true);
			}
		}).catch(() => {
			toast.error('There was an error trying to remove this document. Please try again');
		});
	}
	const updateFileID = (res: { data: { data: TUploadFileRes } }, doc: Partial<TShipmentAddressDoc>, dest: 'pickup' | 'dropoff', index: number) => {
		shipment && setShipment({
			...shipment,
			cargos: shipment.cargos.map((cargo, i) => {
				if (i !== index) return cargo;
				return {
					...cargo,
					[dest]: {
						...cargo[dest],
						shipment_address_documents: cargo[dest].shipment_address_documents?.map((d) => {
							if (d.tempID === doc.tempID) {
								const { tempID, file, ...rest } = d;
								return { ...rest, id: res.data.data.id };
							}
							return d;
						})
					}
				}
			})
		});
	}
	const removeErrorFile = (doc: Partial<TShipmentAddressDoc>, dest: 'pickup' | 'dropoff', index: number) => {
		shipment && setShipment({
			...shipment,
			cargos: shipment.cargos.map((cargo, i) => {
				if (i !== index) return cargo;
				return {
					...cargo,
					[dest]: {
						...cargo[dest],
						shipment_address_documents: cargo[dest].shipment_address_documents?.filter((d) => {
							return d.tempID !== doc.tempID;
						})
					}
				}
			})
		});
		toast.error('An error occured trying to upload your document');
	}
	const updateVal = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, dest: 'pickup' | 'dropoff', index: number) => {
		const { name, value } = e.target;
		const update = {
			...shipment,
			cargos: shipment?.cargos.map((cargo, i) => {
				if (i !== index) return cargo;
				return {
					...cargo,
					[dest]: {
						...cargo[dest],
						[name]: value
					}
				}
			})
		} as TShipmentOrder
		setShipment(update);
		if (ctx?.setTouched) ctx.setTouched(true);
	}
	const updatePPE = (e: ChangeEvent<HTMLInputElement>, dest: 'pickup' | 'dropoff', index: number) => {
		const { checked } = e.target;
		const update = {
			...shipment,
			cargos: shipment?.cargos.map((cargo, i) => {
				if (i !== index) return cargo;
				return {
					...cargo,
					[dest]: {
						...cargo[dest],
						needs_ppe: checked
					}
				}
			})
		} as TShipmentOrder
		setShipment(update);
		if (ctx?.setTouched) ctx.setTouched(true);
	}
	const getDateTimes = (dest: 'pickup' | 'dropoff', index: number) => {
		const theDate = formatDate(new Date(shipment?.cargos[index][dest === 'pickup' ? 'pickup_at' : 'drop_at'] ?? new Date()), 'DATE_WITH_SHORT_MONTH_NAME');
		const timeFrom = dest === 'pickup' ? shipment?.cargos[index].pick_from : shipment?.cargos[0].drop_from;
		const timeTo = dest === 'pickup' ? shipment?.cargos[index].pick_to : shipment?.cargos[0].drop_to;
		return `${theDate} | ${timeFrom} - ${timeTo}`;
	}
	const generateStopTile = (dest: 'pickup' | 'dropoff', index: number, stopIndex: number) => {
		const cargo = shipment?.cargos[index];
		// The stop's own ordinal, falling back to render order for shipments booked before it was stored.
		const stopNumber = (dest === 'pickup' ? cargo?.pick_position : cargo?.drop_position) ?? stopIndex;
		return (
			<div ref={(el) => (subpages.current[stopIndex] = el)}>
				<Tile stretch>
					<div className={`${styles.row} ${styles.justifyContent}`} style={{ justifyContent: 'space-between' }}>
						<Caption>Stop {stopNumber + 1}</Caption>
						<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} CustomIcon={dest === 'pickup' ? ArrowUpIcon : ArrowDownIcon} size={CHIP_SIZE.SMALL} />
					</div>
					<H4>{shipment?.cargos[index][dest === 'pickup' ? 'pickup' : 'dropoff'].address}</H4>
					<Text>{getDateTimes(dest, index)}</Text>
					<Spacer size={20} />
					<TextArea label={'Driver Instructions'} name={'instructions'} value={shipment?.cargos[index][dest].instructions ?? ''} onChange={(e) => updateVal(e, dest, index)} disabled={inputsDisabled} />
					<Grid columns={2}>
						<TextInput label={'Gate reference (optional)'} name={'gate_house'} value={shipment?.cargos[index][dest].gate_house ?? ''} onChange={(e) => updateVal(e, dest, index)} disabled={inputsDisabled} />
						<Checkbox name={'needs_ppe'} value={''} label={'Driver needs PPE'} checked={shipment?.cargos[index][dest].needs_ppe ?? false} onChange={(e) => updatePPE(e, dest, index)} disabled={inputsDisabled} />
					</Grid>
					<UploadButton text={'Upload Cargo Doc'} onFileChange={(files: FileList | null) => addFilesForUpload(files, dest, index)} type={BUTTON_TYPES.SECONDARY} disabled={documentsDisabled} />
					<Spacer size={16} />
					<div className={styles.rowText}>
						<InfoOutlineIcon />
						<Text>Accepted file formats: pdf, jpg, png, tiff up to 80Mb.</Text>
					</div>
					{shipment?.cargos[index][dest].shipment_address_documents?.map((doc, i) => {
						return <FileUploadChip
							key={i}
							name={doc.document?.name ?? ''}
							id={doc.id ? `${doc.id}` : doc.tempID ? doc.tempID : doc.shipmentAPIid ?? ''}
							removeFile={(id: string | number) => { if (!documentsDisabled) removeFile(id, dest, index); }}
							fileToUpload={doc.file}
							uploadProps={{
								url: `/api/documents`,
								payloadFileKey: 'file',
								onUploadSuccess: (res) => updateFileID(res, doc, dest, index),
								onUploadFailure: () => removeErrorFile(doc, dest, index)
							}}
						/>
					})}
				</Tile>
			</div>
		)
	}
	const onPagesScroll = () => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const curPos = pagesRef.current.scrollTop;
			isScrollRef.current = curPos;
			setTimeout(() => {
				if (curPos !== isScrollRef.current) {
					isScrollRef.current = -1;
				}
			}, 100);
			subpages.current.forEach((sp, i) => {
				if (sp) {
					const threshold = 200;
					if (curPos < sp.offsetTop && curPos > sp.offsetTop - threshold) {
						context.setActiveSubMenu(i);
					}
				}
			});
		}
	}
	useEffect(() => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const spTops = subpages.current.map((sp, i) => {
				if (i === 0) return 0;
				if (sp) return sp.offsetTop ?? 0;
				return 0;
			});
			if (isScrollRef.current < 0) {
				pagesRef.current?.scrollTo({
					top: context.activeSubMenu === 0 ? spTops[0] : spTops[context.activeSubMenu],
					behavior: 'smooth'
				});
			}
		}
		if (pagesRef && pagesRef.current) {
			pagesRef.current.addEventListener('scroll', onPagesScroll);
		}
		return () => {
			pagesRef && pagesRef.current && pagesRef.current.removeEventListener('scroll', onPagesScroll);
		}
	}, [context, pagesRef]);
	return (
		<>
			<H3>Update Information for Drivers</H3>
			<Spacer size={16} />
			<Text>Provide clear instructions for drivers and upload relevant cargo documents to ensure a smooth delivery process.</Text>
			<Spacer size={16} />
			{pickupGroups.map((group, groupIndex) => {
				return <Fragment key={groupIndex}>
					{generateStopTile('pickup', group[0], groupIndex)}
					<Spacer size={16} />
				</Fragment>
			})}
			{/* The drop-off panels take the slots after the pick-up panels, matching the menu order. */}
			{dropoffGroups.map((group, i) => (
				<Fragment key={`dropoff-${i}`}>
					{generateStopTile('dropoff', group[0], pickupGroups.length + i)}
				</Fragment>
			))}
		</>
	);
}
